import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export interface Reward {
  id: number;
  points: string;
  amount: string;
  is_default?: number;
  created_at?: string;
  updated_at?: string;
}

export interface RewardFormData {
  id?: number;
  points: string;
  amount: string;
  is_default?: boolean;
}

export interface RewardResponse {
  error: boolean;
  message: string;
  data?: Reward | Reward[];
}

export interface RewardRequest {
  id?: number;
  points: string;
  amount: string;
  is_default?: boolean;
}

export interface SetDefaultRequest {
  id: number;
}

// Validation helpers
export const validateAmount = (value: string): boolean => {
  const num = parseFloat(value);
  return !isNaN(num) && num >= 0;
};

export const validatePoints = (value: string): boolean => {
  const num = parseFloat(value);
  return !isNaN(num) && num >= 0;
};

export const rewardApi = createApi({
  reducerPath: 'rewardApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_SERVER_ENDPOINT,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('admin_token');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set('authkey', '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3');
      headers.set('Content-Type', 'application/x-www-form-urlencoded');
      return headers;
    },
  }),
  tagTypes: ['Reward'],
  endpoints: (build) => ({
    // List all rewards
    listRewards: build.query<RewardResponse, void>({
      query: () => ({
        url: 'user/rewardconversionlist',
        method: 'POST',
        body: new URLSearchParams(),
      }),
      providesTags: ['Reward'],
      transformResponse: (response: any) => {
        console.log('List rewards response:', response);
        return {
          error: response.error || false,
          message: response.message || '',
          data: response.data || [],
        };
      },
    }),

    // Get single reward by ID
    getReward: build.query<RewardResponse, number>({
      query: (id) => ({
        url: 'user/rewardconversionlist',
        method: 'POST',
        body: new URLSearchParams({ id: id.toString() }),
      }),
      providesTags: (result, error, id) => [{ type: 'Reward', id }],
      transformResponse: (response: any) => ({
        error: response.error || false,
        message: response.message || '',
        data: response.data?.[0] || null,
      }),
    }),

    // Add new reward
    addReward: build.mutation<RewardResponse, RewardRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('points', data.points.trim());
        formData.append('amount', data.amount.trim());
        
        if (data.is_default === true) {
          formData.append('is_default', '1');
        }

        console.log('Add reward request:', Object.fromEntries(formData));
        return {
          url: 'user/rewardconversion',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Reward'],
    }),

    // Update existing reward
    updateReward: build.mutation<RewardResponse, RewardRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        if (data.id) {
          formData.append('id', data.id.toString());
        }
        formData.append('points', data.points.trim());
        formData.append('amount', data.amount.trim());
        
        if (data.is_default === true) {
          formData.append('is_default', '1');
        }

        console.log('Update reward request:', Object.fromEntries(formData));
        return {
          url: 'user/rewardconversion',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (result, error, { id }) => [
        'Reward',
        { type: 'Reward', id },
      ],
    }),

    // Set default reward
    setDefaultReward: build.mutation<RewardResponse, SetDefaultRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('id', data.id.toString());
        formData.append('is_default', '1');

        console.log('Set default request:', Object.fromEntries(formData));
        return {
          url: 'user/rewardconversion',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Reward'],
    }),
  }),
});

export const {
  useListRewardsQuery,
  useGetRewardQuery,
  useAddRewardMutation,
  useUpdateRewardMutation,
  useSetDefaultRewardMutation,
} = rewardApi;